/**
 * Shared modal shell (§7g).
 *
 * Minimal — backdrop + centred card, closes on backdrop click or
 * Escape key. No animation library; CSS transition on the backdrop
 * opacity and card transform handles the fade/scale.
 *
 * Used by: CreateProjectModal, PersonaPickerModal, delete confirmations.
 *
 * Accessibility concerns (Escape dismissal, focus trap, body scroll
 * lock, focus restore, initial focus) live in `useModalAccessibility`
 * so future dialog surfaces can reuse the same contract without
 * duplicating the `useEffect` body (G8).
 *
 *   - `role="dialog"` + `aria-modal="true"` on the card
 *   - If the caller has a child with `autoFocus`, that element wins
 *     initial focus; otherwise the dialog card itself receives focus
 *     (via tabIndex=-1) so screen readers announce the dialog
 */

import { useRef } from "react";
import { useModalAccessibility } from "../hooks/useModalAccessibility.js";

export default function Modal({ open, onClose, title, children, wide = false }) {
  const cardRef = useRef(null);
  useModalAccessibility({ open, containerRef: cardRef, onClose });

  if (!open) return null;

  return (
    <div className="modal-backdrop" onClick={onClose} aria-hidden="false">
      <div
        ref={cardRef}
        tabIndex={-1}
        className={`modal-card ${wide ? "modal-card-wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>{title}</h2>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}
